/**
 * Hooks for Magic Bridge FK resolution.
 *
 * Provides React Query hooks for:
 * - Fetching the join tree with magic bridge analysis
 * - Getting cascaded reference options
 * - Resolving magic bridge records
 */

import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import type {
  ViewJoinTreeResponse,
  CascadedReferenceOptionsRequest,
  BridgeResolutionRequest,
  BridgeResolutionResponse,
  ReferenceOptionsResponse,
  CascadingSelection,
} from "../types/magicBridge";

/**
 * Query key factory for magic bridge queries
 */
export const magicBridgeKeys = {
  all: ["magicBridge"] as const,
  joinTree: (workspaceId: string, viewId: string) =>
    [...magicBridgeKeys.all, "joinTree", workspaceId, viewId] as const,
  cascadedOptions: (
    workspaceId: string,
    viewId: string,
    joinId: string,
    parentSelections: Record<string, string>,
    search?: string,
  ) =>
    [
      ...magicBridgeKeys.all,
      "cascadedOptions",
      workspaceId,
      viewId,
      joinId,
      parentSelections,
      search,
    ] as const,
};

/**
 * Hook to fetch the join tree for a view with magic bridge analysis.
 *
 * Returns the hierarchical join structure showing which joins are
 * "magic bridges" (invisible intermediates that need sub-join resolution).
 *
 * @param viewId - View ID
 * @param enabled - Whether to enable the query (default: true)
 */
export function useViewJoinTree(viewId: string | undefined, enabled = true) {
  const authFetch = useAuthFetch();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  return useQuery({
    queryKey: magicBridgeKeys.joinTree(workspaceId, viewId || ""),
    queryFn: async () => {
      const response = await authFetch(
        `/api/workspace/myvault/views/${viewId}/join-tree`,
        "fetch join tree",
        { headers: { "x-workspace-id": workspaceId } },
      );
      return response as ViewJoinTreeResponse;
    },
    enabled: !!viewId && !!workspaceId && enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes - tree structure doesn't change often
  });
}

/**
 * Hook to fetch cascaded reference options.
 *
 * When user selects Person "John Smith", this returns only
 * LegalEntity options where John Smith has an Employment.
 *
 * @param viewId - View ID
 * @param joinId - The join to fetch options for
 * @param parentSelections - Map of alias -> selected ID for filtering
 * @param search - Search term
 * @param enabled - Whether to enable the query
 */
export function useCascadedReferenceOptions(
  viewId: string | undefined,
  joinId: string | undefined,
  parentSelections: Record<string, string>,
  search = "",
  enabled = true,
) {
  const authFetch = useAuthFetch();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  return useQuery({
    queryKey: magicBridgeKeys.cascadedOptions(
      workspaceId,
      viewId || "",
      joinId || "",
      parentSelections,
      search,
    ),
    queryFn: async () => {
      const request: CascadedReferenceOptionsRequest = {
        joinId: joinId!,
        parentSelections,
        search,
        page: 1,
        pageSize: 50,
      };
      const response = await authFetch(
        `/api/workspace/myvault/views/${viewId}/cascaded-reference-options`,
        "fetch cascaded options",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-workspace-id": workspaceId,
          },
          body: JSON.stringify(request),
        },
      );
      return response as ReferenceOptionsResponse;
    },
    enabled: !!viewId && !!joinId && !!workspaceId && enabled,
    staleTime: 1000 * 30, // 30 seconds
  });
}

/**
 * Hook for resolving a magic bridge record.
 *
 * Given selections for Person and LegalEntity, finds the Employment
 * record(s) that match.
 *
 * Returns a mutation function that can be called imperatively.
 */
export function useResolveBridge() {
  const authFetch = useAuthFetch();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  return useMutation({
    mutationFn: async ({
      viewId,
      bridgeJoinId,
      subJoinSelections,
    }: {
      viewId: string;
      bridgeJoinId: string;
      subJoinSelections: Record<string, string>;
    }) => {
      const request: BridgeResolutionRequest = {
        bridgeJoinId,
        subJoinSelections,
      };
      const response = await authFetch(
        `/api/workspace/myvault/views/${viewId}/resolve-bridge`,
        "resolve bridge",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-workspace-id": workspaceId,
          },
          body: JSON.stringify(request),
        },
      );
      return response as BridgeResolutionResponse;
    },
  });
}

/**
 * Helper function to check if a view has magic bridges.
 */
export function hasMagicBridges(
  joinTree: ViewJoinTreeResponse | undefined,
): boolean {
  return (joinTree?.magicBridges?.length ?? 0) > 0;
}

/**
 * Helper function to get all aliases that need to be selected for a magic bridge.
 */
export function getMagicBridgeAliases(
  joinTree: ViewJoinTreeResponse | undefined,
): string[] {
  if (!joinTree?.magicBridges) return [];

  const aliases: string[] = [];
  for (const bridge of joinTree.magicBridges) {
    for (const resJoin of bridge.resolutionJoins) {
      if (!aliases.includes(resJoin.tableAlias)) {
        aliases.push(resJoin.tableAlias);
      }
    }
  }
  return aliases;
}

/**
 * Helper function to find which bridge a given alias is resolving.
 */
export function findBridgeForAlias(
  joinTree: ViewJoinTreeResponse | undefined,
  alias: string,
): { bridgeJoinId: string; targetFkColumn: string } | undefined {
  if (!joinTree?.magicBridges) return undefined;

  for (const bridge of joinTree.magicBridges) {
    const isResolutionJoin = bridge.resolutionJoins.some(
      (rj) => rj.tableAlias === alias,
    );
    if (isResolutionJoin) {
      return {
        bridgeJoinId: bridge.bridgeJoin.joinId,
        targetFkColumn: bridge.targetFkColumn,
      };
    }
  }
  return undefined;
}

/**
 * Helper function to check if all resolution joins for a bridge are selected.
 */
export function isBridgeResolvable(
  joinTree: ViewJoinTreeResponse | undefined,
  bridgeJoinId: string,
  selections: Record<string, CascadingSelection>,
): boolean {
  if (!joinTree?.magicBridges) return false;

  const bridge = joinTree.magicBridges.find(
    (b) => b.bridgeJoin.joinId === bridgeJoinId,
  );
  if (!bridge) return false;

  return bridge.resolutionJoins.every(
    (rj) => selections[rj.tableAlias]?.selectedId,
  );
}

/**
 * Helper function to get sub-join selections for a bridge.
 */
export function getBridgeSubSelections(
  joinTree: ViewJoinTreeResponse | undefined,
  bridgeJoinId: string,
  selections: Record<string, CascadingSelection>,
): Record<string, string> {
  if (!joinTree?.magicBridges) return {};

  const bridge = joinTree.magicBridges.find(
    (b) => b.bridgeJoin.joinId === bridgeJoinId,
  );
  if (!bridge) return {};

  const subSelections: Record<string, string> = {};
  for (const rj of bridge.resolutionJoins) {
    const selection = selections[rj.tableAlias];
    if (selection?.selectedId) {
      subSelections[rj.tableAlias] = selection.selectedId;
    }
  }
  return subSelections;
}
